import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import api from '../../lib/api';

const STORAGE_KEY = 'cookie_consent';

export function getCookieConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

/**
 * KVKK / cookie consent banner. Shown on public pages until the visitor makes a
 * choice. Accepting enables analytics (AnalyticsLoader listens for the
 * 'cookie-consent-changed' event); rejecting keeps tracking off.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [legalSlug, setLegalSlug] = useState(null);

  useEffect(() => {
    if (!getCookieConsent()) {
      // Small delay so it doesn't fight with first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    api.get('/legal-pages')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) setLegalSlug(list[0].slug);
      })
      .catch(() => {});
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
      window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: choice }));
    } catch (e) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[300] p-3 sm:p-4"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label="Çerez onayı"
    >
      <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-[var(--shadow-elev-2,0_10px_40px_rgba(0,0,0,0.25))] p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.12)] flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[hsl(var(--gold))]" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Web sitemizde deneyiminizi iyileştirmek ve ziyaret istatistiklerini ölçmek için
            çerezler kullanıyoruz. “Kabul Et” diyerek çerez kullanımını onaylayabilirsiniz.
            {legalSlug && (
              <>
                {' '}
                <Link to={`/yasal/${legalSlug}`} className="text-[hsl(var(--gold))] underline underline-offset-2 hover:opacity-80">
                  Detaylı bilgi
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => decide('rejected')}
            className="px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-secondary transition-colors"
            data-testid="cookie-reject-button"
          >
            Reddet
          </button>
          <button
            type="button"
            onClick={() => decide('accepted')}
            className="px-5 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors"
            data-testid="cookie-accept-button"
          >
            Kabul Et
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
